/**
 * i18n Foundation — Impact Core
 *
 * React Context-based translation system with localStorage persistence.
 * Supports all 24 EU official languages.
 * Language changes trigger instant re-render across ALL consuming components.
 *
 * Usage:
 *   // Wrap app in <TranslationProvider>
 *   const { t, language, setLanguage } = useTranslation();
 *   <span>{t('nav.dashboard')}</span>
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { en } from './translations/en';
import { fr } from './translations/fr';
import { bg } from './translations/bg';
import { hr } from './translations/hr';
import { cs } from './translations/cs';
import { da } from './translations/da';
import { nl } from './translations/nl';
import { et } from './translations/et';
import { fi } from './translations/fi';
import { de } from './translations/de';
import { el } from './translations/el';
import { hu } from './translations/hu';
import { ga } from './translations/ga';
import { it } from './translations/it';
import { lv } from './translations/lv';
import { lt } from './translations/lt';
import { mt } from './translations/mt';
import { pl } from './translations/pl';
import { pt } from './translations/pt';
import { ro } from './translations/ro';
import { sk } from './translations/sk';
import { sl } from './translations/sl';
import { es } from './translations/es';
import { sv } from './translations/sv';

export type Language =
  | 'bg' | 'hr' | 'cs' | 'da' | 'nl' | 'en' | 'et' | 'fi'
  | 'fr' | 'de' | 'el' | 'hu' | 'ga' | 'it' | 'lv' | 'lt'
  | 'mt' | 'pl' | 'pt' | 'ro' | 'sk' | 'sl' | 'es' | 'sv';

const STORAGE_KEY = 'ic_language';

const ALL_LANGUAGE_CODES: Language[] = [
  'bg', 'hr', 'cs', 'da', 'nl', 'en', 'et', 'fi',
  'fr', 'de', 'el', 'hu', 'ga', 'it', 'lv', 'lt',
  'mt', 'pl', 'pt', 'ro', 'sk', 'sl', 'es', 'sv',
];

const translations: Record<Language, Record<string, string>> = {
  bg, hr, cs, da, nl, en, et, fi,
  fr, de, el, hu, ga, it, lv, lt,
  mt, pl, pt, ro, sk, sl, es, sv,
};

/**
 * Supported language metadata for UI rendering.
 * Listed alphabetically by native name for user convenience.
 */
export const LANGUAGES: { code: Language; label: string; nativeLabel: string }[] = [
  { code: 'bg', label: 'Bulgarian', nativeLabel: 'Български' },
  { code: 'cs', label: 'Czech', nativeLabel: 'Čeština' },
  { code: 'da', label: 'Danish', nativeLabel: 'Dansk' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch' },
  { code: 'et', label: 'Estonian', nativeLabel: 'Eesti' },
  { code: 'el', label: 'Greek', nativeLabel: 'Ελληνικά' },
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español' },
  { code: 'fr', label: 'French', nativeLabel: 'Français' },
  { code: 'ga', label: 'Irish', nativeLabel: 'Gaeilge' },
  { code: 'hr', label: 'Croatian', nativeLabel: 'Hrvatski' },
  { code: 'it', label: 'Italian', nativeLabel: 'Italiano' },
  { code: 'lv', label: 'Latvian', nativeLabel: 'Latviešu' },
  { code: 'lt', label: 'Lithuanian', nativeLabel: 'Lietuvių' },
  { code: 'hu', label: 'Hungarian', nativeLabel: 'Magyar' },
  { code: 'mt', label: 'Maltese', nativeLabel: 'Malti' },
  { code: 'nl', label: 'Dutch', nativeLabel: 'Nederlands' },
  { code: 'pl', label: 'Polish', nativeLabel: 'Polski' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português' },
  { code: 'ro', label: 'Romanian', nativeLabel: 'Română' },
  { code: 'sl', label: 'Slovenian', nativeLabel: 'Slovenščina' },
  { code: 'sk', label: 'Slovak', nativeLabel: 'Slovenčina' },
  { code: 'fi', label: 'Finnish', nativeLabel: 'Suomi' },
  { code: 'sv', label: 'Swedish', nativeLabel: 'Svenska' },
];

/**
 * Get the stored language preference, falling back to 'en'.
 */
function getStoredLanguage(): Language {
  if (typeof window === 'undefined') return 'en';
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && ALL_LANGUAGE_CODES.includes(stored as Language)) return stored as Language;
  return 'en';
}

// ─── React Context ──────────────────────────────────────────────

interface TranslationContextValue {
  t: (key: string) => string;
  language: Language;
  setLanguage: (lang: Language) => void;
}

const TranslationContext = createContext<TranslationContextValue | null>(null);

/**
 * TranslationProvider — wrap the app root to enable shared i18n state.
 * When setLanguage is called, ALL consumers of useTranslation() re-render instantly.
 */
export function TranslationProvider({ children }: { children: ReactNode }) {
  const [language, setLang] = useState<Language>(getStoredLanguage);

  const t = useCallback(
    (key: string): string => {
      return translations[language]?.[key] ?? translations['en']?.[key] ?? key;
    },
    [language],
  );

  const setLanguage = useCallback((lang: Language) => {
    setLang(lang);
    localStorage.setItem(STORAGE_KEY, lang);
  }, []);

  return (
    <TranslationContext.Provider value={{ t, language, setLanguage }}>
      {children}
    </TranslationContext.Provider>
  );
}

/**
 * Translation hook.
 *
 * Returns:
 *   t(key) — resolve a translation key to the current language string
 *   language — current active language code
 *   setLanguage — switch to a different language (persisted + instant re-render)
 */
export function useTranslation(): TranslationContextValue {
  const ctx = useContext(TranslationContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within a <TranslationProvider>');
  }
  return ctx;
}
